import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  Calendar,
  ArrowLeft,
  Share2,
  Copy,
  Check,
  Twitter,
  Linkedin,
  Facebook,
} from "lucide-react";
import { BlogHeader } from "@/components/Blog/BlogHeader";
import { BlogCard } from "@/components/Blog/BlogCard";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { getPostBySlug, getRelatedPosts, mergePosts, getAllPosts, BlogPost } from "@/data/blogData";
import { fetchGeneratedPostBySlug, fetchGeneratedPosts } from "@/services/blogService";
import { SEO } from "@/components/SEO";
import { cn } from "@/lib/utils";
import { isSubdomain } from "@/lib/subdomain";

// Simple markdown-like parser (basic implementation)
function parseContent(content: string): string {
  return (
    content
      // Headers
      .replace(
        /^### (.*$)/gm,
        '<h3 class="text-xl font-bold mt-8 mb-4">$1</h3>',
      )
      .replace(
        /^## (.*$)/gm,
        '<h2 class="text-2xl font-bold mt-10 mb-4">$1</h2>',
      )
      .replace(
        /^# (.*$)/gm,
        '<h1 class="text-3xl font-bold mt-12 mb-6">$1</h1>',
      )
      // Bold
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      // Inline code
      .replace(
        /`([^`]+)`/g,
        '<code class="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">$1</code>',
      )
      // Code blocks
      .replace(/```(\w+)?\n([\s\S]*?)```/g, (_match, lang, code) => {
        return `<pre class="bg-muted/50 border border-border rounded-lg p-4 overflow-x-auto my-6"><code class="text-sm font-mono">${code.trim()}</code></pre>`;
      })
      // Blockquotes
      .replace(
        /^> (.*$)/gm,
        '<blockquote class="border-l-4 border-foreground/20 pl-4 my-6 italic text-muted-foreground">$1</blockquote>',
      )
      // Unordered lists
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal">$1</li>')
      // Wrap consecutive list items
      .replace(
        /(<li class="ml-4 list-disc">.*<\/li>\n?)+/g,
        (match) => `<ul class="my-4 space-y-2">${match}</ul>`,
      )
      .replace(
        /(<li class="ml-4 list-decimal">.*<\/li>\n?)+/g,
        (match) => `<ol class="my-4 space-y-2">${match}</ol>`,
      )
      // Tables (basic)
      .replace(/\|(.+)\|/g, (match) => {
        const cells = match.split("|").filter((c) => c.trim());
        const isHeader = match.includes("---");
        if (isHeader) return "";
        const cellType = "td";
        const cellClass = "border border-border px-4 py-2";
        return `<tr>${cells.map((c) => `<${cellType} class="${cellClass}">${c.trim()}</${cellType}>`).join("")}</tr>`;
      })
      // Paragraphs
      .replace(
        /^(?!<[hluobt]|<pre|<code|<block)(.+)$/gm,
        '<p class="my-4 leading-relaxed">$1</p>',
      )
      // Clean up empty paragraphs
      .replace(/<p class="my-4 leading-relaxed"><\/p>/g, "")
  );
}

interface BlogDetailPageProps {
  onMobileMenuOpen?: () => void;
}

export function BlogDetailPage({ onMobileMenuOpen }: BlogDetailPageProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const postSlug = slug;

    let cancelled = false;

    async function loadPost() {
      const staticPost = getPostBySlug(postSlug);
      if (staticPost) {
        if (cancelled) return;
        setPost(staticPost);
        const generated = await fetchGeneratedPosts();
        const pool = mergePosts(getAllPosts(), generated);
        setRelatedPosts(getRelatedPosts(postSlug, 3, pool));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const generatedPost = await fetchGeneratedPostBySlug(postSlug);
      if (cancelled) return;

      if (generatedPost) {
        setPost(generatedPost);
        const generated = await fetchGeneratedPosts();
        const pool = mergePosts(getAllPosts(), generated);
        setRelatedPosts(getRelatedPosts(postSlug, 3, pool));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (isSubdomain("blog")) {
        navigate("/", { replace: true });
      } else {
        navigate("/blog", { replace: true });
      }
    }

    void loadPost();

    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post?.title || "");

    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    };

    window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${post.title} | DevDotCom Blog`}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.coverImage}
      />

      <BlogHeader showBackButton onMobileMenuOpen={onMobileMenuOpen} />

      {/* Hero / Cover Image */}
      <section className="pt-14 relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative h-[40vh] md:h-[50vh] lg:h-[60vh] overflow-hidden"
        >
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </motion.div>
      </section>

      {/* Article Header */}
      <section className="relative -mt-32 md:-mt-40 pb-12">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            {/* Category & Reading Time */}
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 text-sm font-medium bg-foreground text-background rounded-full">
                {post.category}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                {post.readTime} min read
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-border">
              {/* Author */}
              <div className="flex items-center gap-3">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium">{post.author.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {post.author.role}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {formattedDate}
              </div>

              {/* Share */}
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleShare("twitter")}
                  aria-label="Share on Twitter"
                >
                  <Twitter className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleShare("linkedin")}
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Article Content */}
            <div
              className="prose prose-lg dark:prose-invert max-w-none pt-8"
              dangerouslySetInnerHTML={{ __html: parseContent(post.content) }}
            />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-sm bg-muted text-muted-foreground rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Author Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 p-6 md:p-8 bg-muted/50 border border-border rounded-2xl"
            >
              <div className="flex flex-col sm:flex-row gap-6">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-bold text-lg mb-1">
                    Written by {post.author.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    {post.author.role} at DevDotCom
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Passionate about building great software and sharing
                    knowledge with the developer community.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.article>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-8">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost, index) => (
                  <BlogCard
                    key={relatedPost.id}
                    post={relatedPost}
                    index={index}
                    variant="default"
                    onClick={() =>
                      navigate(
                        isSubdomain("blog")
                          ? `/${relatedPost.slug}`
                          : `/blog/${relatedPost.slug}`,
                      )
                    }
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Back to Blog CTA */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate(isSubdomain("blog") ? "/" : "/blog")}
            className="group"
          >
            <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Back to All Articles
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
